import { useTranslation } from "react-i18next";
import { ShieldCheck, Warning, HandHeart, GlobeHemisphereWest, Sparkle, LockKey, AppleLogo, GooglePlayLogo } from "@phosphor-icons/react";
import SEOHead from "../components/SEOHead";
import { SPALink } from "../context/NavigationContext";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.luminawallet.app";
const APP_STORE_URL = "https://apps.apple.com/fr/app/lumina-wallet/id6760651380";

const VALUES = [
  { icon: ShieldCheck, key: "transparency" },
  { icon: LockKey, key: "sovereignty" },
  { icon: GlobeHemisphereWest, key: "accessible" },
  { icon: HandHeart, key: "honesty" },
];

const AppStoreBadge = () => (
  <a
    href={APP_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="about-cta-appstore"
    aria-label="Download on the App Store"
    className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-black hover:bg-neutral-900 text-white border border-white/10 hover:border-white/30 shadow-lg transition-all"
  >
    <AppleLogo size={32} weight="fill" className="text-white" />
    <span className="flex flex-col text-left leading-tight">
      <span className="text-[10px] uppercase tracking-wider text-gray-400">Download on the</span>
      <span className="text-lg font-semibold">App Store</span>
    </span>
  </a>
);

const PlayStoreBadge = () => (
  <a
    href={PLAY_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="about-cta-playstore"
    aria-label="Get it on Google Play"
    className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-black hover:bg-neutral-900 text-white border border-white/10 hover:border-white/30 shadow-lg transition-all"
  >
    {/* Google Play triangle in official colors */}
    <svg viewBox="0 0 512 512" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="gp1" x1="0" x2="1" y1="0" y2="1"><stop offset="0" stopColor="#00A0FF"/><stop offset="1" stopColor="#00E2FF"/></linearGradient>
        <linearGradient id="gp2" x1="0" x2="1" y1="0" y2="0"><stop offset="0" stopColor="#FFBD00"/><stop offset="1" stopColor="#FFE000"/></linearGradient>
        <linearGradient id="gp3" x1="0" x2="1" y1="0" y2="1"><stop offset="0" stopColor="#FF3A44"/><stop offset="1" stopColor="#C31162"/></linearGradient>
        <linearGradient id="gp4" x1="0" x2="1" y1="0" y2="1"><stop offset="0" stopColor="#32A071"/><stop offset="1" stopColor="#2DA771"/></linearGradient>
      </defs>
      <path fill="url(#gp1)" d="M75 44c-8 8-12 21-12 37v350c0 17 4 30 12 37l1 1 197-196v-33L76 43l-1 1z"/>
      <path fill="url(#gp2)" d="M338 274l-66-66v-33l66-66 1 1 78 44c22 12 22 33 0 45l-79 75z"/>
      <path fill="url(#gp3)" d="M339 273l-67-68L75 468c8 8 20 8 34 1l230-127z"/>
      <path fill="url(#gp4)" d="M339 137L108 6C94-2 82 0 75 8l197 197 67-68z"/>
    </svg>
    <span className="flex flex-col text-left leading-tight">
      <span className="text-[10px] uppercase tracking-wider text-gray-400">Get it on</span>
      <span className="text-lg font-semibold">Google Play</span>
    </span>
  </a>
);

const Section = ({ icon: Icon, tag, title, children, testId }) => (
  <section className="mb-14 md:mb-20" data-testid={testId}>
    <div className="flex items-center gap-3 mb-4">
      {Icon && (
        <div className="w-10 h-10 rounded-xl bg-lumina-violet/10 border border-lumina-violet/20 flex items-center justify-center">
          <Icon size={22} weight="duotone" className="text-lumina-violet" />
        </div>
      )}
      {tag && (
        <span className="text-xs font-bold tracking-widest uppercase text-lumina-violet">{tag}</span>
      )}
    </div>
    <h2 className="text-2xl md:text-4xl font-bold text-white mb-6 leading-tight">{title}</h2>
    <div className="space-y-4 text-gray-300 text-base md:text-lg leading-relaxed">
      {children}
    </div>
  </section>
);

const AboutPage = () => {
  const { t } = useTranslation();
  const problemBullets = t("aboutPage.problem.bullets", { returnObjects: true }) || [];
  const commitments = t("aboutPage.commitment.items", { returnObjects: true }) || [];

  return (
    <main role="main" aria-label={t("aboutPage.seoTitle")} className="min-h-screen bg-lumina-dark pt-20">
      <SEOHead
        title={t("aboutPage.seoTitle")}
        description={t("aboutPage.seoDescription")}
        path="/about"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <header className="mb-16 md:mb-24" data-testid="about-hero">
          <span className="text-xs font-bold tracking-widest uppercase text-lumina-violet mb-4 block">
            {t("aboutPage.hero.tag")}
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            {t("aboutPage.hero.title")}
          </h1>
          <p className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-3xl">
            {t("aboutPage.hero.subtitle")}
          </p>
        </header>

        {/* Section 1 — Why */}
        <Section
          icon={Sparkle}
          tag={t("aboutPage.why.tag")}
          title={t("aboutPage.why.title")}
          testId="about-why"
        >
          <p>{t("aboutPage.why.p1")}</p>
          <p>{t("aboutPage.why.p2")}</p>
          <p className="font-semibold text-white">{t("aboutPage.why.p3")}</p>
        </Section>

        {/* Section 2 — Problem */}
        <Section
          icon={Warning}
          tag={t("aboutPage.problem.tag")}
          title={t("aboutPage.problem.title")}
          testId="about-problem"
        >
          <p>{t("aboutPage.problem.intro")}</p>
          <ul className="space-y-3 list-none pl-0">
            {problemBullets.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <Warning size={18} weight="duotone" className="text-amber-400 flex-shrink-0 mt-1" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="italic text-gray-400">{t("aboutPage.problem.outro")}</p>
        </Section>

        {/* Section 3 — Commitment */}
        <Section
          icon={ShieldCheck}
          tag={t("aboutPage.commitment.tag")}
          title={t("aboutPage.commitment.title")}
          testId="about-commitment"
        >
          <p>{t("aboutPage.commitment.intro")}</p>
          <div className="space-y-6">
            {commitments.map((c) => (
              <div key={c.title} className="border-l-2 border-lumina-violet/50 pl-5">
                <h3 className="text-white font-bold text-lg md:text-xl mb-2">{c.title}</h3>
                <p className="text-gray-300">{c.text}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Section 4 — Who */}
        <Section
          icon={GlobeHemisphereWest}
          tag={t("aboutPage.who.tag")}
          title={t("aboutPage.who.title")}
          testId="about-who"
        >
          <p>{t("aboutPage.who.p1")}</p>
          <p>{t("aboutPage.who.p2")}</p>
          <p>{t("aboutPage.who.p3")}</p>
        </Section>

        {/* Section 5 — Values (4 cards) */}
        <Section
          icon={HandHeart}
          tag={t("aboutPage.values.tag")}
          title={t("aboutPage.values.title")}
          testId="about-values"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            {VALUES.map((v) => (
              <div
                key={v.key}
                className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-lumina-violet/30 transition-all"
                data-testid={`about-value-${v.key}`}
              >
                <v.icon size={24} weight="duotone" className="text-lumina-violet mb-3" />
                <h3 className="text-white font-bold text-base mb-2">
                  {t(`aboutPage.values.items.${v.key}.title`)}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {t(`aboutPage.values.items.${v.key}.text`)}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* Section 6 — CTA */}
        <section
          className="mt-16 md:mt-24 p-8 md:p-12 rounded-3xl bg-gradient-to-br from-lumina-violet/10 to-purple-500/5 border border-lumina-violet/20 text-center"
          data-testid="about-cta"
        >
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            {t("aboutPage.cta.title")}
          </h2>
          <p className="text-gray-300 text-base md:text-lg mb-8 max-w-2xl mx-auto">
            {t("aboutPage.cta.text")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <AppStoreBadge />
            <PlayStoreBadge />
          </div>

          <SPALink
            to="/features"
            className="inline-block mt-8 text-lumina-violet hover:text-white text-sm font-medium underline underline-offset-4 transition-colors"
            data-testid="about-cta-features"
          >
            {t("aboutPage.cta.discoverFeatures")}
          </SPALink>
        </section>
      </div>
    </main>
  );
};

export default AboutPage;
